import { useState, useEffect } from 'react';
import { listAllLibraryPlanTemplates } from '../../graphql/resourceLibraryQueries';
import { importTemplateToCompany } from '../../utils/templateUtils';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import type { LibraryPlanCategory } from '../../types/resourceLibraryTypes';
import { BackButton } from '../ui';

interface ImportFromLibraryViewProps {
    onBack: () => void;
    companyId: string;
    onImportComplete: (planId: string) => void;
}

/**
 * View that shows available library templates for a company and allows
 * importing one as a fully independent plan.
 */
export const ImportFromLibraryView = ({ onBack, companyId, onImportComplete }: ImportFromLibraryViewProps) => {
    const { refreshData, plans } = useSafetyTrainingContext();
    const [templates, setTemplates] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<{ planId: string; topicCount: number; scheduleCount: number } | null>(null);
    const [error, setError] = useState('');

    // Track which template IDs are already imported as plans
    const importedTemplateIds = new Set(
        plans.filter(p => p.sourceTemplateId).map(p => p.sourceTemplateId!)
    );

    useEffect(() => {
        const fetch = async () => {
            try {
                const all = await listAllLibraryPlanTemplates();
                // Only show templates shared with this company
                const available = all.filter(
                    (t: any) => t.status === 'ACTIVE' && t.sharedWithCompanyIds?.includes(companyId)
                );
                setTemplates(available);
            } catch (err) {
                console.error('Error fetching library templates:', err);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [companyId]);

    const handleImport = async (templateId: string) => {
        setImporting(true);
        setError('');
        try {
            const result = await importTemplateToCompany({ templateId, companyId });
            await refreshData();
            setImportResult(result);
        } catch (err: any) {
            console.error('Import failed:', err);
            setError(err.message || 'Import failed. Please try again.');
        } finally {
            setImporting(false);
        }
    };

    if (importResult) {
        return (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                    Plan imported successfully
                </p>
                <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: 24 }}>
                    Created {importResult.topicCount} topics and {importResult.scheduleCount} schedule entries.
                    The plan is saved as a Draft — you can customize it before activating.
                </p>
                <BackButton
                    onClick={() => onImportComplete(importResult!.planId)}
                    buttonText="Back to Dashboard"
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b' }}>
                Loading available templates...
            </div>
        );
    }

    return (
        <div style={{ padding: '32px 24px', maxWidth: 700, margin: '0 auto' }}>
            <h2 style={{ color: '#1e293b', marginBottom: 8 }}>Import from Zorva Library</h2>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
                Import a pre-built training plan template. You'll get a full independent copy with all topics
                and schedule structure. You can edit it freely without affecting the original template.
            </p>

            {error && (
                <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</p>
            )}

            {templates.length === 0 ? (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 24, textAlign: 'center', color: '#64748b' }}>
                    No templates are available for your company yet.
                    Contact your Zorva administrator to get templates assigned.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {templates.map((template: any) => {
                        let catCount = 0;
                        let topicCount = 0;
                        try {
                            const cats = JSON.parse(template.categories) as LibraryPlanCategory[];
                            catCount = cats.length;
                            topicCount = cats.reduce((sum, c) => sum + (c.topicRefs?.length ?? 0), 0);
                        } catch { /* */ }

                        return (
                            <div
                                key={template.id}
                                style={{
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 10,
                                    padding: '16px 20px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                                        {template.name}
                                    </div>
                                    <div style={{ fontSize: 13, color: '#64748b' }}>
                                        {template.governingStandard} · {template.cycleDuration} days · {template.minimumTimeRequired} min · {catCount} categories · {topicCount} topics
                                    </div>
                                </div>
                                <button
                                    className="adv-btn-manage"
                                    onClick={() => handleImport(template.id)}
                                    disabled={importing || importedTemplateIds.has(template.id)}
                                    style={{
                                        whiteSpace: 'nowrap',
                                        opacity: importedTemplateIds.has(template.id) ? 0.5 : 1,
                                    }}
                                >
                                    {importedTemplateIds.has(template.id)
                                        ? '✓ Already Imported'
                                        : importing ? 'Importing...' : '📥 Import'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <BackButton onClick={onBack} buttonText="Back to Dashboard" />
        </div>
    );
};
